import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
} from '@tanstack/react-query';
import {
  applyTransaction,
  type AcceptInvitationResponse,
  type ClientEventInput,
  type ConnectionDetail,
  type ConnectionsResponse,
  type CreateTransactionInput,
  type DisputeInput,
  type FeedbackInput,
  type Invitation,
  type InvitationDraft,
  type InvitationPreview,
  type InvitationSummary,
  type Me,
  type Transaction,
  type TransactionListResponse,
  type TransactionWithEvents,
  type UpdateMeInput,
  type UpdateTransactionInput,
} from '@debtbot/shared';
import { api } from './client';

export const queryKeys = {
  me: ['me'] as const,
  connections: ['connections'] as const,
  connection: (id: string) => ['connection', id] as const,
  transactions: (connectionId: string) => ['transactions', connectionId] as const,
  transaction: (id: string) => ['transaction', id] as const,
  invitationPreview: (token: string) => ['invitation', token] as const,
  invitations: ['invitations'] as const,
};

// ---------- me ----------

export function useMe() {
  return useQuery({ queryKey: queryKeys.me, queryFn: () => api<Me>('/me'), staleTime: 5 * 60_000 });
}

export function useUpdateMe() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateMeInput) => api<Me>('/me', { method: 'PATCH', body: input }),
    onSuccess: (me) => qc.setQueryData(queryKeys.me, me),
  });
}

/**
 * Единственное событие аналитики, которое пишет клиент (белый список на сервере).
 * Ошибка не показывается: аналитика не должна мешать действию, ради которого она пишется.
 */
export function useTrackEvent() {
  return useMutation({
    mutationFn: (input: ClientEventInput) =>
      api<void>('/analytics/event', { method: 'POST', body: input }),
  });
}

export function useSendFeedback() {
  return useMutation({
    mutationFn: (input: FeedbackInput) => api<void>('/feedback', { method: 'POST', body: input }),
  });
}

// ---------- connections ----------

export function useConnections() {
  return useQuery({
    queryKey: queryKeys.connections,
    queryFn: () => api<ConnectionsResponse>('/connections'),
  });
}

export function useConnection(id: string) {
  const qc = useQueryClient();
  return useQuery({
    queryKey: queryKeys.connection(id),
    queryFn: () => api<ConnectionDetail>(`/connections/${id}`),
    enabled: id !== '',
    // мгновенный первый рендер из списка, затем фоновое обновление
    placeholderData: () => {
      const fromList = qc
        .getQueryData<ConnectionsResponse>(queryKeys.connections)
        ?.items.find((c) => c.id === id);
      return fromList ? { ...fromList, recent_presets: [] } : undefined;
    },
  });
}

// ---------- invitations ----------

export type CreateInvitationInput = {
  /** Откуда ушла ссылка ('share'); сервер пишет его в invite_created. */
  source_channel: string;
  /** Черновик первой операции; null — обычное приглашение, ключ в тело не попадает. */
  draft: InvitationDraft | null;
};

export function useCreateInvitation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ source_channel, draft }: CreateInvitationInput) =>
      api<Invitation>('/invitations', {
        method: 'POST',
        body: { source_channel, ...(draft && { draft }) },
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: queryKeys.invitations }),
  });
}

/** Мои живые приглашения. Нужны только на главной без связей, поэтому включаются по месту. */
export function useInvitations(enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.invitations,
    queryFn: () => api<{ items: InvitationSummary[] }>('/invitations'),
    enabled,
  });
}

export function useInvitationPreview(token: string) {
  return useQuery({
    queryKey: queryKeys.invitationPreview(token),
    queryFn: () => api<InvitationPreview>(`/invitations/by-token/${token}`),
    retry: false,
  });
}

export function useAcceptInvitation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (token: string) =>
      api<AcceptInvitationResponse>(`/invitations/by-token/${token}/accept`, { method: 'POST' }),
    onSuccess: ({ connection }) => {
      qc.setQueryData(queryKeys.connection(connection.id), { ...connection, recent_presets: [] });
      void qc.invalidateQueries({ queryKey: queryKeys.connections });
    },
  });
}

// ---------- transactions ----------

type TxPages = InfiniteData<TransactionListResponse, string | null>;

export function useTransactions(connectionId: string) {
  return useInfiniteQuery({
    queryKey: queryKeys.transactions(connectionId),
    queryFn: ({ pageParam }) =>
      api<TransactionListResponse>(
        `/connections/${connectionId}/transactions?limit=50${pageParam ? `&cursor=${encodeURIComponent(pageParam)}` : ''}`,
      ),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.next_cursor,
  });
}

export function useTransaction(id: string) {
  return useQuery({
    queryKey: queryKeys.transaction(id),
    queryFn: () => api<TransactionWithEvents>(`/transactions/${id}`),
    enabled: id !== '',
  });
}

function invalidateConnection(
  qc: ReturnType<typeof useQueryClient>,
  connectionId: string,
  txId?: string,
) {
  void qc.invalidateQueries({ queryKey: queryKeys.transactions(connectionId) });
  void qc.invalidateQueries({ queryKey: queryKeys.connection(connectionId) });
  void qc.invalidateQueries({ queryKey: queryKeys.connections });
  if (txId) void qc.invalidateQueries({ queryKey: queryKeys.transaction(txId) });
}

/**
 * Создание с optimistic update (PROJECT_SPEC §14.4): операция сразу появляется в ленте,
 * баланс пересчитывается той же функцией, что и на сервере. Для «погасить полностью»
 * (expected_balance) optimistic не делаем — нужен вердикт сервера.
 */
export function useCreateTransaction(connectionId: string, meId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateTransactionInput) =>
      api<Transaction>(`/connections/${connectionId}/transactions`, {
        method: 'POST',
        body: input,
      }),
    onMutate: async (input) => {
      if (input.expected_balance) return undefined;
      await Promise.all([
        qc.cancelQueries({ queryKey: queryKeys.transactions(connectionId) }),
        qc.cancelQueries({ queryKey: queryKeys.connection(connectionId) }),
      ]);
      const prevList = qc.getQueryData<TxPages>(queryKeys.transactions(connectionId));
      const prevConn = qc.getQueryData<ConnectionDetail>(queryKeys.connection(connectionId));
      const now = new Date().toISOString();
      const optimistic: Transaction = {
        id: `pending-${input.client_request_id}`,
        connection_id: connectionId,
        type: input.type,
        payer_id: input.payer_id,
        beneficiary_id: '',
        amount: input.amount,
        currency: input.currency,
        category: input.category,
        note: input.note,
        status: 'ACTIVE',
        disputed_by_id: null,
        disputed_at: null,
        dispute_reason: null,
        created_by_id: meId,
        version: 1,
        is_edited: false,
        occurred_at: now,
        deleted_at: null,
        created_at: now,
        updated_at: now,
      };
      qc.setQueryData<TxPages>(queryKeys.transactions(connectionId), (old) => {
        if (!old) return old;
        const [first, ...rest] = old.pages;
        return {
          ...old,
          pages: [
            {
              ...(first ?? { items: [], next_cursor: null }),
              items: [optimistic, ...(first?.items ?? [])],
            },
            ...rest,
          ],
        };
      });
      qc.setQueryData<ConnectionDetail>(queryKeys.connection(connectionId), (old) => {
        if (!old) return old;
        const next = applyTransaction(
          { balances: old.balances, disputed: old.disputed },
          {
            payer_id: input.payer_id,
            amount: input.amount,
            currency: input.currency,
            status: 'ACTIVE',
            deleted_at: null,
          },
          meId,
        );
        return { ...old, ...next, last_activity_at: now };
      });
      return { prevList, prevConn };
    },
    onError: (_err, _input, ctx) => {
      if (!ctx) return;
      qc.setQueryData(queryKeys.transactions(connectionId), ctx.prevList);
      qc.setQueryData(queryKeys.connection(connectionId), ctx.prevConn);
    },
    onSettled: () => invalidateConnection(qc, connectionId),
  });
}

export function useUpdateTransaction(id: string, connectionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateTransactionInput) =>
      api<Transaction>(`/transactions/${id}`, { method: 'PATCH', body: input }),
    onSuccess: () => invalidateConnection(qc, connectionId, id),
  });
}

export function useDeleteTransaction(connectionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<void>(`/transactions/${id}`, { method: 'DELETE' }),
    onSuccess: (_void, id) => invalidateConnection(qc, connectionId, id),
  });
}

export function useDisputeTransaction(id: string, connectionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: DisputeInput) =>
      api<Transaction>(`/transactions/${id}/dispute`, { method: 'POST', body: input }),
    onSuccess: () => invalidateConnection(qc, connectionId, id),
  });
}

export function useWithdrawDispute(id: string, connectionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api<Transaction>(`/transactions/${id}/dispute`, { method: 'DELETE' }),
    onSuccess: () => invalidateConnection(qc, connectionId, id),
  });
}
